import { ACCESS_TYPES, ACCESS_TYPES_CODE, ACCESS_TYPES_NAMES, AccessTypeConstants } from "@/_constant";
import { DateTime } from "luxon";
import pako from "pako";
import parsePhoneNumber from "libphonenumber-js";
import { months } from "moment";
import HashIds from "hashids";

export const pause = (timing) => {
  return new Promise((resolve) => {
    setTimeout(resolve, [timing]);
  });
}

export const timeout = (func, timing) => {
  if (typeof func !== 'function') throw Error('timeout callback is not a function');

  const timer = setTimeout(() => {
    func();
    clearInterval(timer);
  }, timing);
}

export function copyToClipboard(text = "") {
  return new Promise((resolve) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => resolve(true))
        .catch(() => resolve(false));
    } else {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.top = "0";
        textArea.style.left = "0";
        textArea.style.width = "1px";
        textArea.style.height = "1px";
        textArea.style.padding = "0";
        textArea.style.border = "none";
        textArea.style.outline = "none";
        textArea.style.boxShadow = "none";
        textArea.style.background = "transparent";

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);

        resolve(successful);
      } catch (err) {
        console.error("Fallback copy failed:", err);
        resolve(false);
      }
    }
  });
}


/**
 * Decompress Zlib response
 * @param {string} data base64 string
 * return decompressed data 
 */
export function decompressData(data) {
  try {
    const compressedData = Uint8Array.from(atob(data), (char) => char.charCodeAt(0));
    const decompressedData = pako.inflate(compressedData, { to: "string" });
    return JSON.parse(decompressedData);
  } catch (e) {
    console.error("Failed to decompress data");
  }
  return null;
}

function getExtensionFromMime(mime) {
  const map = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/webp": "webp",
    "image/gif": "gif",
    "image/svg+xml": "svg",
    "application/pdf": "pdf",
  };

  return map[mime] ?? "bin"; // fallback if unknown
}

export async function blobUrlToFile(
  blobUrl,
  filename
){
  const res = await fetch(blobUrl);
  const blob = await res.blob();

  return new File([blob], filename, { type: blob.type || "application/octet-stream" });
}


export function dataUrlToFile(dataUrl, filename){
  const [header, base64] = dataUrl.split(",");
  const match = header.match(/data:(.*);base64/);

  if (!match) {
    throw new Error("Invalid data URL");
  }
  
  const mime = match[1];
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  if(!filename){
    const ext = getExtensionFromMime(mime);
    filename = `${Date.now()}.${ext}`;
  }

  return new File([bytes], filename, { type: mime });
}

export function categoryDisplayCode(category) {
  if (!category) return "";
  if (category.category_id === '-1') {
    return "access.customer";
  } else if (category.category_id === '-2') {
    // return "label.agent";
    return "access.business_developer";

  } 
  // else if (category.category_id === '-3') {
  //   return "label.others";
  // } 
  else if (category.category_id === '999') {
    return "label.assignee";
  } 
  // else if(category.category_id === '-4'){
  //   return "access.agri_expert";
  // }
  return category?.category_display_code || category?.access_display_code || "";
}

export function accessTypeName(accessId) {
  if (accessId === null || accessId === undefined) {
    return "";
  }
  const cleanId = String(accessId).trim();
  if (/^\d+$/.test(cleanId)) {
    return ACCESS_TYPES[Number(cleanId)] || "";
  }
  const lowerId = cleanId.toLowerCase();
  if (Object.values(ACCESS_TYPES).includes(lowerId)) {
    return lowerId;
  }
  const normalized = lowerId.replace(/[\s_]+/g, '-');
  if (Object.values(ACCESS_TYPES).includes(normalized)) {
    return normalized;
  }
  const mapped = ACCESS_TYPES_NAMES[cleanId] || AccessTypeConstants[cleanId];
  if (mapped) {
    return mapped;
  }
  const code = ACCESS_TYPES_CODE[cleanId];
  if (code && ACCESS_TYPES[code]) {
    return ACCESS_TYPES[code];
  }
  return cleanId;
}

export function accessTypeUriPathToCode(accessId){
  return ACCESS_TYPES_CODE[accessId]
}

export function toNullableString(value) {
  return value === null || value === undefined || value === "" ? null : String(value);
}

export function toNullableNumber(value) {
  const stringValue = toNullableString(value);
  if (stringValue === null) return null;
  const numberValue = Number(stringValue);
  return Number.isFinite(numberValue) ? numberValue : null;
}

export function formatTimeISOFormat(time, fromFormat) {
  return DateTime.fromFormat(time, fromFormat).toISOTime();
}

export function formatDateISOFormat(date, fromFormat) {
  return DateTime.fromFormat(date, fromFormat).toISODate();
}

export function formatDateFromISOFormat(date, toFormat) {
  return DateTime.fromISO(date).toFormat(toFormat);
}

export function formatTime(time, fromFormat, format) {
  return DateTime.fromFormat(time, fromFormat).toFormat(format);
}

export function formatDate(date, fromFormat, format) {
  return DateTime.fromFormat(date, fromFormat).toFormat(format);
}

export function formatDateTime(datetime, fromFormat, format) {
  return DateTime.fromFormat(datetime, fromFormat).toFormat(format);
}

export function currentISODate() {
  return DateTime.now().toISODate();
}

export function formatFromISODate(date, format='yyyy-MM-dd') {
  return DateTime.fromISO(date).toFormat(format);
}

export function getYearMonth(date, format = 'yyyy-MM') {
  return DateTime.fromISO(date).toFormat(format);
}

export function minusMonth(date, fromFormat, format) {
  return DateTime.fromFormat(date, fromFormat).minus({ months: 1 }).toFormat(format);
}

export function currentMinusMonth(date, month){
  return DateTime.fromISO(date).minus({months: month}).toISODate();
}

export function swapLabel(displayCode = "") {

  if (displayCode === 'category.machinery') {
    return 'category_machinery';
  } else if (displayCode === 'used-machinery-dealer') {
    return 'category_machinery';
  } else if (displayCode === 'category.machinery_traders') {
    return 'category_machinery';
  } else if (displayCode === 'category.livestock') {
    return 'category_livestock';
  } else if (displayCode === 'category.biomass_trader') {
    return 'category_biomass';
  }
  return displayCode;
}

export function headingChange(accessCode) {

  if (accessCode === 'Service Provider') {
    return "label.create_post_start_services";
  } else if (accessCode === "Assignee") {
    return "label.how_it_works_for_assignee";
  } else if (accessCode === "Agent") {
    return "label.how_to_start_earning";
  } else if (accessCode === 'Agri Expert'){
    return "label.expert_story_banner_subtitle";
  }
  return "label.create_post_start_selling";
}

export function getYoutubeLink(selectedData) {

  const getCategoryLink = {
    [AccessTypeConstants.AGENT]: "label.how_to_sell_url_agent",
    [AccessTypeConstants.DEALER]: "label.how_to_sell_url_dealer",
    [AccessTypeConstants.FARMER]: "label.how_to_sell_url_farmer",
    [AccessTypeConstants.OTHERS]: "label.how_to_sell_url_farmer",
    [AccessTypeConstants.SERVICE_PROVIDER]: "label.how_to_sell_url_service_provider",
    "harvesting": "label.how_to_sell_url_harvesting_service",
    "dairy-center": "label.how_to_sell_url_milk_dairy_center",
    "milk-plant": "label.how_to_sell_url_milk_dairy_center",
    "agent": "label.how_to_sell_url_agent"
  }
  if (selectedData.category_id === '999') {
    return getCategoryLink[selectedData?.category_name?.toLowerCase().replaceAll(" ", "-")]
  }

  if (selectedData.category_id === '234') {
    return getCategoryLink[selectedData?.category_name?.toLowerCase().replaceAll(" ", "-")]
  }
  if (selectedData.category_id === '245') {
    return getCategoryLink[selectedData?.category_name?.toLowerCase().replaceAll(" ", "-")]
  }
  if (selectedData.category_id === '267') {
    return getCategoryLink[selectedData?.category_name?.toLowerCase().replaceAll(" ", "-")]
  }
  if (selectedData.category_id === '271') {
    return getCategoryLink[selectedData?.category_name?.toLowerCase().replaceAll(" ", "-")]
  }


  if (selectedData.category_id === '240' || selectedData.category_id === '206') {
    return getCategoryLink[selectedData?.category_name?.toLowerCase().replaceAll(" ", "-")]
  }
  if (selectedData.category_id === '168' || selectedData.category_id === '169') {
    return getCategoryLink[selectedData?.category_name?.toLowerCase().replaceAll(" ", "-")]
  }
  return getCategoryLink[selectedData?.access_name?.toLowerCase().replaceAll(" ", "-")]

}

export function getCategoryNames(item) {

  if (item?.id === 171 || item?.categoryId === 171) {
    return 'category.paddy_straw';
  } else if (item?.id === 172 || item?.categoryId === 172) {
    return 'category.wheat_straw';
  } else if (item?.id === 173 || item?.categoryId === 173) {
    return 'category.sugarcane';
  } else if (item?.id === 172 || item?.categoryId === 172) {
    return 'category.forest_waste';
  } else if (item?.id === 307 || item?.categoryId === 307) {
    return 'category.fruits';
  } else if (item?.id === 308 || item?.categoryId === 308) {
    return 'category.vegetables';
  } else if (item?.id === 115 || item?.categoryId === 115) {
    return 'category.sale';
  } else if (item?.id === 116 || item?.categoryId === 116) {
    return 'category.yearly_contract';
  } else if (item?.id === 117 || item?.categoryId === 117) {
    return 'category.contract_farming';
  } else if (item?.id === 119 || item?.categoryId === 119 || item?.categoryId === 123) {
    return 'category.sale';
  } else if (item?.id === 120 || item?.categoryId === 120) {
    return 'category.yearly_contract';
  } else if (item?.id === 121 || item?.categoryId === 121) {
    return 'category.contract_farming';
  } else if (item?.id === 124 || item?.categoryId === 124) {
    return 'category.rentals';
  } else if (item?.id === 107 || item?.categoryId === 107) {
    return 'category.grains';
  } else if (item?.id === 108 || item?.categoryId === 108) {
    return 'category.pulses';
  } else if (item?.id === 109 || item?.categoryId === 109) {
    return 'category.spices';
  } else if (item?.id === 110 || item?.categoryId === 110) {
    return 'category.dry_fruits';
  } else if (item?.id === 111 || item?.categoryId === 111) {
    return 'category.flowers';
  } else if (item?.id === 112 || item?.categoryId === 112) {
    return 'category.other_crop';
  } else if (item?.id === 105 || item?.categoryId === 105) {
    return 'category.dog';
  } else if (item?.id === 98 || item?.categoryId === 98) {
    return 'category.cow';
  } else if (item?.id === 103 || item?.categoryId === 103) {
    return 'category.goat';
  } else if (item?.id === 104 || item?.categoryId === 104) {
    return 'category.sheep';
  } else if (item?.id === 41 || item?.categoryId === 41) {
    return 'category.tractor';
  } else if (item?.id === 20 || item?.categoryId === 20) {
    return 'category.straw_baler';
  } else if (item?.id === 21 || item?.categoryId === 21) {
    return 'category.sugarcane_baler';
  } else if (item?.id === 22 || item?.categoryId === 22) {
    return 'category.forest_waste_baler';
  } else if (item?.id === 59 || item?.categoryId === 59) {
    return 'category.straw_baler';
  } else if (item?.id === 60 || item?.categoryId === 60) {
    return 'category.sugarcane_reaper';
  } else if (item?.id === 61 || item?.categoryId === 61) {
    return 'category.sugarcane_baler';
  } else if (item?.id === 55 || item?.categoryId === 55) {
    return 'category.sugarcane_reaper';
  } else if (item?.id === 56 || item?.categoryId === 56) {
    return 'category.straw_reaper';
  } else if (item?.id === 183 || item?.categoryId === 183) {
    return 'category.stalian';
  } else if (item?.id === 184 || item?.categoryId === 184) {
    return 'category.gelding';
  } else if (item?.id === 185 || item?.categoryId === 185) {
    return 'category.colt';
  } else if (item?.id === 187 || item?.categoryId === 187) {
    return 'category.filly';
  } else if (item?.id === 57 || item?.categoryId === 57) {
    return 'category.forest_waste_reaper';
  } else if (item?.id === 43 || item?.categoryId === 43) {
    return 'category.combine_harvester';
  } else if (item?.id === 44 || item?.categoryId === 44) {
    return 'category.potato_harvester';
  } else if (item?.id === 45 || item?.categoryId === 45) {
    return 'category.sugarcane_harvester';
  } else if (item?.id === 46 || item?.categoryId === 46) {
    return 'category.carrot_harvester';
  } else if (item?.id === 47 || item?.categoryId === 47) {
    return 'category.cotton_harvester';
  } else if (item?.id === 48 || item?.categoryId === 48) {
    return 'category.groundnut_digger_shaker';
  } else if (item?.id === 49 || item?.categoryId === 49) {
    return 'category.turmeric_harvester';
  } else if (item?.id === 50 || item?.categoryId === 50) {
    return 'category.tapioca_harvester';
  } else if (item?.id === 51 || item?.categoryId === 51) {
    return 'category.coconut_tree_climber';
  } else if (item?.id === 52 || item?.categoryId === 52) {
    return 'category.banana_harvester';
  } else if (item?.id === 53 || item?.categoryId === 53) {
    return 'category.tea_plucking_machine';
  }

  return null;
}

export function toggleDropdown(selector) {
  if (!selector) {
    console.error("selector value is required to toggle dropdown");
    return;
  }
  const dropdown = document.getElementById(selector);
  window.bootstrap.Dropdown.getOrCreateInstance(dropdown.lastChild).toggle();
}

export function capitalize(str) {
  if (!str || typeof str !== 'string') return str; // Handle edge cases
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function validateMobileNumber(phoneNumber) {
  const no = parsePhoneNumber(phoneNumber, 'IN');
  return no.isValid()
}

export function formatMobileNumber(phoneNumber) {
  const no = parsePhoneNumber(phoneNumber, 'IN');
  return no.formatNational();
}

export function comparePhoneNumber(phoneNumber = '', phoneNumber2) {
  const no1 = parsePhoneNumber(phoneNumber, 'IN');
  const no2 = parsePhoneNumber(phoneNumber2, 'IN');
  return no1.nationalNumber === no2.nationalNumber;
}

export function selectedAccessType() {
  return sessionStorage.getItem('selectedDropAccessType');
}

export function selectedUserAccessId() {
  return sessionStorage.getItem('selectedUserAccessId');
}

export function selectedCategoryId() {
  return sessionStorage.getItem('selectedDrop');
}

export function selectedCategoryName() {
  return sessionStorage.getItem("selectedCategoryName");
}

export function groupByArrayToMap(array, groupByKey) {
  return array.reduce((acc, item) => {
    const key = item[groupByKey];
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});
}

export const encodeParamsToUrl = (params) => {
  const searchParams = new URLSearchParams(params);
  return searchParams.toString();  // returns the query string
}

export function getTimeAgo(createdDate) {
  const now = DateTime.now();
  const created = DateTime.fromISO(createdDate); // Assumes ISO string format
  const diff = now.diff(created, ['days', 'hours', 'minutes']);

  if (diff.days < 1) {
    if (diff.hours >= 1) {
      return `${Math.floor(diff.hours)} hour${diff.hours >= 2 ? 's' : ''} ago`;
    } else if (diff.minutes >= 1) {
      return `${Math.floor(diff.minutes)} minute${diff.minutes >= 2 ? 's' : ''} ago`;
    } else {
      return `just now`;
    }
  } else if (diff.days < 7) {
    return `${Math.floor(diff.days)} day${diff.days >= 2 ? 's' : ''} ago`;
  } else {
    return created.toFormat('LLL d, yyyy'); // e.g., Apr 3, 2024
  }
}
/**
* Debounce function to limit the rate at which a function is called.
* @param {Function} func - The function to be debounced (e.g., API call).
* @param {number} delay - The debounce delay in milliseconds.
* @returns {Function} - A debounced version of the input function.
*/
export function debounce(func, delay = 300) {
  let debounceTimeout;

  return function (event) {
    // Clear the previous timeout to cancel the previous API call
    clearTimeout(debounceTimeout);

    // Set a new timeout to call the function after the specified delay
    debounceTimeout = setTimeout(() => {
      func(event);
    }, delay);
  }
}

const hash = new HashIds();
export function hashEncode(string){
  return hash.encode(string);
}

export function hashDecode(string){
  return hash.decode(string)[0];
}

export function fileSizeInMB(size){
  return (size / (1024 * 1024)).toFixed(2);
}

export function hasKey(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

export function getValue(obj={}, key=""){
  return hasKey(obj, key) ? obj[key] : "";
}

export function getUserAccessCategoryId(userAccess = {}) {
  const categoryId =
    userAccess.category_id ??
    userAccess.categoryId ??
    userAccess.category?.id ??
    userAccess.categories?.id;
  return categoryId === null || categoryId === undefined ? "" : String(categoryId);
}

export function findUserAccess(userAccessList = [], userAccessId = "", categoryKey = "") {
  const normalizedId = String(userAccessId || "");
  const normalizedKey = String(categoryKey || "").toLowerCase();
  return userAccessList.find((userAccess) => {
    const accessId = String(userAccess?.id ?? userAccess?.userAccessId ?? "");
    const accessKey = String(
      userAccess?.category_key ??
      userAccess?.categoryKey ??
      userAccess?.category?.key ??
      userAccess?.categories?.key ??
      ""
    ).toLowerCase();
    return (normalizedId && accessId === normalizedId) ||
      (normalizedKey && accessKey === normalizedKey);
  });
}
